import React, { useState, useEffect } from 'react';
import { Tooltip, Container, OverlayTrigger } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppSelector } from '../../store/store';
import { getActiveBrand, getActiveBrandOnBoardedStatus } from '../../store/selector/brand.selector';
import { useLazyGetBrandVoiceQuery } from '../../store/api/brandMagic.api';
import { IBrandVoiceState } from '../../interfaces/brand-magic.interface';
import BrandMagicScrapingURL from '../../components/brandProfile/BrandMagicScrapingURL';
import BrandVoice from '../../components/brandProfile/BrandVoice';
import DesignElements from '../../components/brandProfile/DesignElements';
import Memory from '../../components/brandProfile/Memory';
import Stepper from '../../components/appElement/Stepper';

const BrandProfile = () => {
    const activeBrandOnBoardedStatus = useAppSelector(getActiveBrandOnBoardedStatus);
    const activeBrand = useAppSelector(getActiveBrand);
    const [fetchBrandVoice, { isFetching: isFetchingData }] = useLazyGetBrandVoiceQuery();
    const [clickedStep, setClickedStep] = useState<null | number>(null);
    const steps = ['Brand Analysis', 'Brand Voice', 'Design Elements', 'Memory'];
    const stepsForOnboardedBrand = ['Brand Analysis', 'Brand Voice', 'Design Elements', 'Memory'];
    const stepsToBeUsed = !activeBrandOnBoardedStatus ? steps : stepsForOnboardedBrand;
    const [currentStep, setCurrentStep] = useState(0);
    const [error, setError] = useState(''); // State for error message
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const [brandVoice, setBrandVoice] = useState<IBrandVoiceState>({
        brandName: '',
        keyProductService: '',
        keyMessages: '',
        tone: '',
        scrapedTextContent: '',
        anythingElse: '',
        brandId: 0,
    });

    useEffect(() => {
        const fetchData = async () => {
            if (!activeBrand) return;
            const response = await fetchBrandVoice(activeBrand.id);
            const data = response.data;
            setBrandVoice({
                brandName: data && data.brandName ? data.brandName : '',
                keyProductService: data && data.keyProductService ? data.keyProductService : '',
                keyMessages: data && data.keyMessages ? data.keyMessages : '',
                tone: data && data.tone ? data.tone : '',
                scrapedTextContent: data && data.scrapedTextContent ? data.scrapedTextContent : '',
                anythingElse: data && data.anythingElse ? data.anythingElse : '',
                brandId: activeBrand.id,
            });
        };

        fetchData();
    }, [activeBrand]);

    const handleNext = () => {
        setClickedStep(null);
        const lenghtOfArray = (): number => {
            return !activeBrandOnBoardedStatus
                ? stepsForm.length - 1
                : stepsForOnboardedCustomers.length - 1;
        };
        if (currentStep < lenghtOfArray()) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePrev = () => {
        setClickedStep(null);
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const stepsForm = [
        <BrandMagicScrapingURL
            handleNext={handleNext}
            error={error}
            setError={setError}
            setSuccessMessage={setSuccessMessage}
            brandVoice={brandVoice}
        />,
        <BrandVoice
            handleNext={handleNext}
            handlePrev={handlePrev}
            error={error}
            isFetchingBrandVoice={isFetchingData}
            successMessage={successMessage}
            brandVoice={brandVoice}
            setBrandVoice={setBrandVoice}
        />,
        <DesignElements handleNext={handleNext} handlePrev={handlePrev} />,
        <Memory
            handlePrev={handlePrev}
            brandVoice={brandVoice}
            setBrandVoice={setBrandVoice}
            isFetchingData={isFetchingData}
        />,
    ];
    const stepsForOnboardedCustomers = [
        <BrandMagicScrapingURL
            handleNext={handleNext}
            error={error}
            setError={setError}
            setSuccessMessage={setSuccessMessage}
            brandVoice={brandVoice}
        />,
        <BrandVoice
            handleNext={handleNext}
            handlePrev={handlePrev}
            error={error}
            isFetchingBrandVoice={isFetchingData}
            successMessage={successMessage}
            brandVoice={brandVoice}
            setBrandVoice={setBrandVoice}
        />,
        <DesignElements handleNext={handleNext} handlePrev={handlePrev} />,
        <Memory
            handlePrev={handlePrev}
            brandVoice={brandVoice}
            setBrandVoice={setBrandVoice}
            isFetchingData={isFetchingData}
        />,
    ];

    const isStepActive = (index: number) => {
        if (
            (activeBrandOnBoardedStatus && currentStep === index) ||
            (!activeBrandOnBoardedStatus && currentStep === index)
        ) {
            return 'active';
        }
        return '';
    };

    const handleClick = (index: number) => {
        setClickedStep(index);
    };
    const isMobile = window.innerWidth <= 768;
    return (
        <Container>
            <div className="brandMagic position-relative">
                {activeBrandOnBoardedStatus ? (
                    <div className=" d-flex justify-content-center align-items-end stepper brandMagic_stepper mt-4 mb-4">
                        {stepsToBeUsed?.map((name, index) => (
                            <div
                                key={name}
                                className={`step ${isStepActive(index)}`}
                                onClick={() => handleClick(index)}
                            >
                                <p className="m-0 stepName">{name}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="d-flex justify-content-center align-items-center stepper onboard_stepper mt-4 mb-4">
                        {stepsToBeUsed?.map((name, index) => (
                            <div
                                key={name}
                                className={`d-flex align-items-center me-3 ${
                                    currentStep === index ? 'activeStep' : ''
                                }`}
                                onClick={() => handleClick(index)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="onboard_step d-flex justify-content-center align-items-center fw-bold p-3 rounded-circle me-2">
                                    {index + 1}
                                </div>
                                <p className="stepName">{name}</p>
                                {index !== stepsToBeUsed.length - 1 && (
                                    <div className="stepperLine me-2 ms-2"></div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                <Stepper
                    steps={activeBrandOnBoardedStatus ? stepsForOnboardedCustomers : stepsForm}
                    currentStep={currentStep}
                    setCurrentStep={setCurrentStep}
                    clickedStep={clickedStep}
                />
                {activeBrandOnBoardedStatus ? (
                    <div
                        className="custom_nav-profile position-fixed start-0 m-3"
                        style={{ top: isMobile ? '100px' : '56px', zIndex: 1080 }}
                    >
                        <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 250, hide: 400 }}
                            overlay={<Tooltip>Back to Apprentice</Tooltip>}
                        >
                            <motion.button
                                onClick={() => navigate('/')}
                                className="btn btn-primary iconbtn rounded-circle "
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                            >
                                <i className="ph ph-arrow-up-left lg"></i>
                            </motion.button>
                        </OverlayTrigger>
                    </div>
                ) : (
                    <></>
                )}
            </div>
        </Container>
    );
};

export default BrandProfile;
